(function () {
  'use strict';

  const OCS_EVENT_PREFIX = 'ocs:';
  const STORAGE_KEY = 'ocs:analytics:pdp-origin:v1';
  const TOKEN_TTL_MS = 120000;
  const TOKEN_EXPIRY_MS = 120001;
  const STANDARD_EVENT_NAMES = ['view_item', 'add_to_cart', 'remove_from_cart', 'purchase'];

  const VIEW_ITEM_EVENT = 'ocs:view_item';
  const FILTER_INTERACTION_EVENT = 'ocs:filter_interaction';

  /**
   * One view_item payload, built at the moment of the click that is navigating
   * the shopper to a product page, waiting to be published by the page it is
   * navigating to.
   *
   * A view published from a click is published microseconds before the document
   * unloads, and it has to travel theme -> Web Pixel sandbox -> dataLayer -> GTM
   * tag -> /g/collect before the sandbox is torn down. GTM loads asynchronously
   * inside that sandbox, so on a cold container the push only queues and the
   * event is lost outright. The click is the only place that knows which surface
   * the shopper was on; the destination is the only place that can reliably
   * deliver. So the click builds the payload and the destination sends it.
   *
   * Separate from STORAGE_KEY on purpose. The origin token answers "where did
   * this visit come from" and is written by every surface that declares an origin
   * boundary — PLP hits, search suggestions, cart upsells — most of which
   * publish nothing on click. This carries an actual event.
   */
  const DEFERRED_VIEW_KEY = 'ocs:analytics:deferred-view:v1';

  const ACCOUNT_LOCATION_EVENT = 'ocs:myaccount_loginlocation';

  /**
   * The same problem as DEFERRED_VIEW_KEY, for the header account click.
   *
   * Production reported this from a GTM DOM click trigger, which cannot run in
   * the Web Pixel sandbox, so the theme publishes it instead. The signed-in
   * desktop chip is an <a> to the account page, so that publish lands
   * microseconds before the document unloads and dies in the same race a
   * click-side view_item does. The guest chip opens a popover and the mobile
   * control opens a drawer; neither navigates, so neither needs this.
   *
   * Its own key rather than a shared one: the two events are consumed by
   * different code at different moments, and one envelope per destination is
   * what makes consuming unconditionally safe.
   */
  const DEFERRED_ACCOUNT_KEY = 'ocs:analytics:deferred-account:v1';
  const CTA_EVENT = 'ocs:cta';
  const DEFERRED_CTA_KEY = 'ocs:analytics:deferred-cta:v1';
  const ACCOUNT_CTA_EVENT = 'ocs:account_cta';
  const DEFERRED_ACCOUNT_CTA_KEY = 'ocs:analytics:deferred-account-cta:v1';

  // Private document initialization claim
  const initializedDocuments = new WeakSet();

  /**
   * @typedef {Object} OriginToken
   * @property {number} committedAt
   * @property {number} createdAt
   * @property {string} sourceKind
   * @property {number|null} sourceIndex
   * @property {string} sourcePathname
   * @property {string} sourceValue
   * @property {string} targetPathname
   * @property {number} v
   */

  /**
   * @typedef {Object} PendingOwner
   * @property {string} serialized
   * @property {any} timerId
   * @property {Document} document
   * @property {string} documentPathname
   * @property {any} token
   * @property {MouseEvent} event
   */

  // Private pending token owner for preventDefault cancellation and expiry
  /** @type {PendingOwner|null} */
  let pendingOwner = null;

  /**
   * @typedef {Object} NormalizedViewSnapshot
   * @property {string} itemId
   * @property {string} itemName
   * @property {string} itemVariant
   * @property {string} brand
   * @property {string} category
   * @property {string} category2
   * @property {string} category3
   * @property {string|number} priceCents
   * @property {string} ontarioGrown - Exact 'Yes', 'No', or empty string
   * @property {string} craft - Exact 'Yes', 'No', or empty string
   * @property {string} priceDrop - Exact 'Yes' or 'No'
   * @property {string} new - Exact 'Yes', 'No', or empty string
   * @property {string} itemAvailability - Exact 'In-Stock', 'In-Store Only', or 'Out of Stock'
   * @property {number} [index] - One-based position in the initiating list
   * @property {string} [itemListName]
   */

  /**
   * @typedef {Object} ViewItem
   * @property {string} item_id
   * @property {string} item_name
   * @property {string} item_brand
   * @property {string} item_variant
   * @property {string} item_category
   * @property {string} item_category2
   * @property {string} item_category3
   * @property {number} price
   * @property {string} currency
   * @property {number} index
   * @property {string} ontario_grown
   * @property {string} craft
   * @property {string} price_drop
   * @property {string} new
   * @property {string} item_availability
   * @property {string|undefined} [item_list_name]
   */

  /**
   * @typedef {Object} RawAddItem
   * @property {string} itemId
   * @property {string} itemName
   * @property {string} itemVariant
   * @property {string} brand
   * @property {string} category
   * @property {string} category2
   * @property {string} category3
   * @property {string|number} priceCents
   * @property {string|number} [compareAtCents]
   * @property {string|boolean} ontarioGrown
   * @property {string|boolean} craft
   * @property {string} variantStatus
   * @property {string} [itemAvailability]
   * @property {string|boolean} [variantAvailable]
   * @property {string|boolean} [variantInStore]
   * @property {number} quantity
   * @property {string} [itemListName]
   */

  /**
   * @typedef {Object} AddItem
   * @property {string} item_id
   * @property {string} item_name
   * @property {string} item_brand
   * @property {string} item_variant
   * @property {string} item_category
   * @property {string} item_category2
   * @property {string} item_category3
   * @property {number} price
   * @property {string} ontario_grown
   * @property {string} craft
   * @property {string} price_drop
   * @property {string} new
   * @property {string} item_availability
   * @property {number} quantity
   * @property {string|undefined} [item_list_name]
   */

  /**
   * @typedef {Object} RemoveSnapshot
   * @property {string} itemId
   * @property {string} itemName
   * @property {string} itemVariant
   * @property {string} brand
   * @property {string} category
   * @property {string} category2
   * @property {string} category3
   * @property {number} priceCents
   * @property {string} ontarioGrown
   * @property {string} craft
   * @property {string} priceDrop
   * @property {string} new
   * @property {number} quantity
   */

  /**
   * @typedef {Object} RemoveItem
   * @property {string} item_id
   * @property {string} item_name
   * @property {string} item_brand
   * @property {string} item_variant
   * @property {string} item_category
   * @property {string} item_category2
   * @property {string} item_category3
   * @property {number} price
   * @property {string} ontario_grown
   * @property {string} craft
   * @property {string} price_drop
   * @property {string} new
   * @property {number} quantity
   * @property {string} item_list_name
   */

  /**
   * @typedef {Object} RawCartMetadata
   * @property {string} itemId
   * @property {string} brand
   * @property {string} category
   * @property {string} category2
   * @property {string} category3
   * @property {string|number} [compareAtCents]
   * @property {string|number} priceCents
   * @property {string|boolean} ontarioGrown
   * @property {string|boolean} craft
   * @property {string} variantStatus
   * @property {string} [itemListName]
   */

  /**
   * The same-origin product link whose click is being dispatched right now, or
   * null between clicks.
   *
   * NOT scoped by a microtask, and this is the whole reason the first attempt at
   * this de-duplication shipped dead. During a real user click the browser
   * invokes each listener with an empty JavaScript stack, so a microtask
   * checkpoint runs after *every listener* rather than at the end of the
   * dispatch. A microtask queued from the capture-phase writer below therefore
   * runs before the card's own bubble-phase handler is ever called, and the
   * publisher sees null. (Under `element.click()` the stack is not empty and the
   * checkpoint is deferred to the end of the dispatch, which is why a test that
   * drains microtasks after `dispatchEvent` cannot catch it.)
   *
   * Scoping is instead two things: `eventPhase`, which the DOM resets to NONE
   * the instant dispatch completes and is therefore an exact synchronous test
   * for "still being dispatched", and a task-scheduled release as the outer
   * bound for anything that does not report a phase.
   *
   * @type {{targetPathname: string, targetSearch: string, event: MouseEvent}|null}
   */
  let inFlightLinkClick = null;

  /**
   * Record that a click on a same-origin link is in flight.
   *
   * Called for every such click — any destination, boundary or not — because
   * the publishers this serves are not the ones that declare boundaries, and
   * they do not all navigate to products. Each caller of deferToNavigation
   * decides which destinations are its own.
   *
   * @param {string} targetPathname
   * @param {string} targetSearch
   * @param {MouseEvent} event
   */
  function noteLinkClick(targetPathname, targetSearch, event) {
    inFlightLinkClick = { targetPathname, targetSearch, event };
    const release = function () {
      if (inFlightLinkClick && inFlightLinkClick.event === event) inFlightLinkClick = null;
    };
    // A task, not a microtask: a task runs after the whole dispatch, which is
    // the scope the click-side publishers actually occupy.
    if (typeof setTimeout === 'function') setTimeout(release, 0);
  }

  /**
   * Hand an event to the page the in-flight click is navigating to, instead of
   * publishing it here where it would not survive the unload.
   *
   * @param {string} storageKey
   * @param {object} payload
   * @param {(pathname: string, search: string) => boolean} isAllowedTarget The destinations this
   *   event is allowed to be deferred to. A click heading anywhere else is not
   *   this event's navigation.
   * @returns {boolean} True when the event was deferred and must not be
   *   published by the caller. False means there is no navigation to defer to
   *   and the caller should publish normally.
   */
  function deferToNavigation(storageKey, payload, isAllowedTarget) {
    const click = inFlightLinkClick;
    if (!click) return false;

    // The exact test for "this click is still being dispatched". `eventPhase` is
    // CAPTURING/AT_TARGET/BUBBLING during dispatch and NONE once it is over, so
    // an event published later for some unrelated reason can never inherit a
    // retired target even if its release task has not run yet.
    if (click.event.eventPhase === 0) {
      inFlightLinkClick = null;
      return false;
    }

    // The recorder accepts every same-origin link click, so the destination is
    // checked here rather than assumed. Without this an account click would
    // swallow a view, and a product click would swallow an account event.
    if (!isAllowedTarget(click.targetPathname, click.targetSearch)) return false;

    // The theme editor is not a shopper journey. Publish in place so the
    // debugger still shows something, and leave nothing behind.
    const shopify = /** @type {any} */ (window).Shopify;
    if (shopify?.designMode) return false;

    const envelope = { v: 1, targetPathname: click.targetPathname, payload, deferredAt: Date.now() };
    let serialized;
    try {
      serialized = JSON.stringify(envelope);
      sessionStorage.setItem(storageKey, serialized);
    } catch {
      // Nowhere to defer to. Publishing here is worse than not reporting, but
      // only marginally, and it is the behaviour that shipped before this.
      return false;
    }

    // Same cancellation shape as the origin token: a click that ends up
    // defaultPrevented is not a navigation, so what it deferred has no
    // destination and is dropped once the dispatch is over.
    const withdrawIfCanceled = function () {
      if (!click.event.defaultPrevented) return;
      try {
        if (sessionStorage.getItem(storageKey) === serialized) {
          sessionStorage.removeItem(storageKey);
        }
      } catch {}
    };
    // A task for the same reason the release above is one: `defaultPrevented` is
    // only final once every listener has run, and a microtask queued here would
    // be checked after this listener rather than after the dispatch.
    if (typeof setTimeout === 'function') setTimeout(withdrawIfCanceled, 0);

    return true;
  }

  /**
   * @param {object} payload A payload already built by createViewItemPayload.
   * @returns {boolean}
   */
  function deferViewItem(payload) {
    return deferToNavigation(DEFERRED_VIEW_KEY, payload, isProductPath);
  }

  /**
   * @param {object} payload The { login_location, cta_button } pair.
   * @returns {boolean}
   */
  function deferAccountLocation(payload) {
    return deferToNavigation(DEFERRED_ACCOUNT_KEY, payload, isAccountPath);
  }

  /**
   * Defer a same-origin CTA only when it changes pathname. Hash links and
   * same-page controls have no destination document to consume the envelope.
   *
   * @param {object} payload The six-field CTA payload.
   * @returns {boolean}
   */
  function deferNavigatingCta(storageKey, payload) {
    return deferToNavigation(
      storageKey,
      payload,
      (targetPathname, targetSearch) =>
        targetPathname !== window.location.pathname || targetSearch !== window.location.search
    );
  }

  function deferCta(payload) {
    return deferNavigatingCta(DEFERRED_CTA_KEY, payload);
  }

  function deferAccountCta(payload) {
    return deferNavigatingCta(DEFERRED_ACCOUNT_CTA_KEY, payload);
  }

  /**
   * Read and consume a deferred envelope. Consumed unconditionally, exactly as
   * the origin token is, so an envelope for some other destination cannot
   * survive to be published by a later page.
   *
   * Validates the envelope only. What counts as a usable payload differs per
   * event, so that stays with the caller.
   *
   * @param {string} storageKey
   * @param {number} now
   * @returns {{targetPathname: string, payload: any}|undefined}
   */
  function readDeferredEnvelope(storageKey, now) {
    let stored;
    try {
      stored = sessionStorage.getItem(storageKey);
      sessionStorage.removeItem(storageKey);
    } catch {
      return undefined;
    }

    if (!stored) return undefined;

    let envelope;
    try {
      envelope = JSON.parse(stored);
    } catch {
      return undefined;
    }

    if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) return undefined;

    const expectedKeys = ['deferredAt', 'payload', 'targetPathname', 'v'];
    const actualKeys = Object.keys(envelope).sort();
    if (actualKeys.length !== expectedKeys.length) return undefined;
    for (let i = 0; i < expectedKeys.length; i += 1) {
      if (actualKeys[i] !== expectedKeys[i]) return undefined;
    }

    if (envelope.v !== 1) return undefined;
    if (typeof envelope.targetPathname !== 'string' || !envelope.targetPathname) return undefined;
    if (!Number.isInteger(envelope.deferredAt) || envelope.deferredAt <= 0) return undefined;
    if (envelope.deferredAt > now) return undefined;
    if (now - envelope.deferredAt > TOKEN_TTL_MS) return undefined;

    return { targetPathname: envelope.targetPathname, payload: envelope.payload };
  }

  /**
   * Read and consume a deferred view.
   *
   * Every rejection is a fail-open: the caller publishes its own view instead,
   * so a malformed envelope costs the origin attribution, never the event.
   *
   * @param {string} currentPathname
   * @param {number} now
   * @returns {object|undefined} The payload to publish, or undefined.
   */
  function consumeDeferredViewItem(currentPathname, now) {
    const envelope = readDeferredEnvelope(DEFERRED_VIEW_KEY, now);
    if (!envelope) return undefined;

    if (envelope.targetPathname !== currentPathname) return undefined;
    if (!isProductPath(currentPathname)) return undefined;

    // The payload came from createViewItemPayload in this same file, so this
    // re-checks only what a tampered store could break: the controlled location
    // vocabulary the pixel switches on, and that there is an item to report.
    const payload = envelope.payload;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
    if (!VALID_LOCATIONS.includes(payload.location)) return undefined;
    if (!Array.isArray(payload.items) || payload.items.length === 0) return undefined;
    for (const item of payload.items) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return undefined;
    }

    return payload;
  }

  /**
   * Read and consume a deferred header account click.
   *
   * @param {string} currentPathname
   * @param {number} now
   * @returns {object|undefined} The payload to publish, or undefined.
   */
  function consumeDeferredAccountLocation(currentPathname, now) {
    const envelope = readDeferredEnvelope(DEFERRED_ACCOUNT_KEY, now);
    if (!envelope) return undefined;

    /*
      Deliberately not the exact pathname match the view uses. A view names a
      product, so publishing it on the wrong page would be a lie. This payload is
      a constant pair meaning "the account control in the global header was
      clicked", so any account destination is a true place to report it — and
      that tolerance is what survives the /account -> /account/login redirect a
      signed-out session gets, and the guest controls that link straight to the
      login page.

      Still fails closed off the account section entirely: landing anywhere else
      means the navigation was not the one this was deferred to.
    */
    if (!isAccountPath(envelope.targetPathname)) return undefined;
    if (!isAccountPath(currentPathname)) return undefined;

    const payload = envelope.payload;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;

    const keys = Object.keys(payload).sort();
    if (keys.length !== 2 || keys[0] !== 'cta_button' || keys[1] !== 'login_location') return undefined;
    if (payload.login_location !== 'global_header') return undefined;
    if (typeof payload.cta_button !== 'string' || !payload.cta_button) return undefined;

    return payload;
  }

  /**
   * Consume a CTA only on its exact same-origin destination. The CTA publisher
   * always emits this fixed six-field shape, so anything else is stale or
   * tampered storage and is discarded rather than sent to GA4.
   *
   * @param {string} currentPathname
   * @param {number} now
   * @returns {object|undefined}
   */
  function consumeDeferredCta(currentPathname, now) {
    const envelope = readDeferredEnvelope(DEFERRED_CTA_KEY, now);
    if (!envelope || envelope.targetPathname !== currentPathname) return undefined;

    const payload = envelope.payload;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;

    const expectedKeys = ['age_verified', 'cta_title_pos', 'index', 'locale', 'section', 'section_heading'];
    const keys = Object.keys(payload).sort();
    if (keys.length !== expectedKeys.length) return undefined;
    for (let i = 0; i < expectedKeys.length; i += 1) {
      if (keys[i] !== expectedKeys[i]) return undefined;
    }

    for (const key of ['cta_title_pos', 'locale', 'section', 'section_heading']) {
      if (typeof payload[key] !== 'string') return undefined;
    }
    if (payload.age_verified !== true && payload.age_verified !== false) return undefined;
    if (payload.index !== '' && (!Number.isInteger(payload.index) || payload.index <= 0)) return undefined;

    return payload;
  }

  function isAccountLogoutPath(pathname) {
    const root = (/** @type {any} */ (window).Shopify)?.routes?.root;
    return typeof root === 'string' && pathname === `${root}account/logout`;
  }

  /** Consume the fixed two-field account CTA payload on its destination. */
  function consumeDeferredAccountCta(currentPathname, now) {
    const envelope = readDeferredEnvelope(DEFERRED_ACCOUNT_CTA_KEY, now);
    if (!envelope) return undefined;

    // Shopify redirects /account/logout to the localized storefront root. The
    // logout target itself never renders a document that could consume this.
    if (envelope.targetPathname !== currentPathname && !isAccountLogoutPath(envelope.targetPathname)) {
      return undefined;
    }

    const payload = envelope.payload;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;

    const keys = Object.keys(payload).sort();
    if (keys.length !== 2 || keys[0] !== 'cta_title_pos' || keys[1] !== 'section') return undefined;
    if (typeof payload.cta_title_pos !== 'string' || typeof payload.section !== 'string') return undefined;

    return payload;
  }

  /**
   * Theme code owns only prefixed OCS custom customer events.
   * OCS Custom Pixel owners map these events, plus Shopify standard customer events, to GA4 or GTM.
   * Do not publish raw standard event names from theme code: view_item, add_to_cart, remove_from_cart, or purchase.
   *
   * @param {string} name
   * @param {object} payload
   * @returns {boolean}
   */
  function publishOcsAnalyticsEvent(name, payload) {
    if (typeof name !== 'string' || !name.startsWith(OCS_EVENT_PREFIX) || name.length <= OCS_EVENT_PREFIX.length) {
      return false;
    }

    if (STANDARD_EVENT_NAMES.includes(name)) {
      return false;
    }

    const Shopify = /** @type {{ analytics?: { publish?: (name: string, payload: object) => void } }} */ (window.Shopify);
    if (typeof Shopify?.analytics?.publish !== 'function') {
      return false;
    }

    // A view reported from a click on a product link is reported from the wrong
    // side of a navigation: see deferViewItem. Hand it to the destination
    // instead. Returns true either way — the caller asked for the view to be
    // reported and it will be, just not from here.
    if (name === VIEW_ITEM_EVENT) {
      try {
        if (deferViewItem(payload)) return true;
      } catch (error) {
        // Deferral is best-effort. Fall through and publish in place.
      }
    }

    // The signed-in header chip is an <a> to the account page, so this event is
    // published from the wrong side of a navigation too. The guest controls do
    // not navigate and fall straight through. See deferAccountLocation.
    if (name === ACCOUNT_LOCATION_EVENT) {
      try {
        if (deferAccountLocation(payload)) return true;
      } catch (error) {
        // Deferral is best-effort. Fall through and publish in place.
      }
    }

    // Same-origin CTA links have the same unload race as product-card views.
    // Hand them to the destination; buttons, external links, new tabs, and
    // same-page links still publish immediately from the source document.
    if (name === CTA_EVENT) {
      try {
        if (deferCta(payload)) return true;
      } catch (error) {
        // Deferral is best-effort. Fall through and publish in place.
      }
    }

    if (name === ACCOUNT_CTA_EVENT) {
      try {
        if (deferAccountCta(payload)) return true;
      } catch (error) {
        // Deferral is best-effort. Fall through and publish in place.
      }
    }

    let publishPayload = payload;
    if (name === FILTER_INTERACTION_EVENT && payload && typeof payload === 'object' && !Array.isArray(payload)) {
      publishPayload = {
        ...payload,
        location: payload.page_type === 'search' ? 'search' : 'plp',
      };
      delete publishPayload.event_source;
    }

    try {
      Shopify.analytics.publish(name, publishPayload);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Normalize a boolean string or boolean value to exact "Yes" or "No".
   * Missing and invalid values default to No so ecommerce badge fields remain stable.
   *
   * @param {unknown} value
   * @returns {'Yes'|'No'}
   */
  function normalizeBooleanString(value) {
    if (value === 'Yes' || value === true || value === 'true') return 'Yes';
    return 'No';
  }

  /**
   * Compute New status from the exact variant status string.
   * Exact "new" returns Yes; every other value defaults to No.
   *
   * @param {unknown} variantStatus
   * @returns {'Yes'|'No'}
   */
  function normalizeNew(variantStatus) {
    return variantStatus === 'new' ? 'Yes' : 'No';
  }

  /**
   * Compute Price Drop from authoritative compare-at and price cents.
   * Only a valid compare-at amount greater than a valid product price returns Yes;
   * every missing, false, or invalid input defaults to No.
   *
   * @param {unknown} compareAtCents
   * @param {unknown} priceCents
   * @returns {'Yes'|'No'}
   */
  function normalizePriceDrop(compareAtCents, priceCents) {
    const price = Number(priceCents);
    const compareAt = Number(compareAtCents);

    if (!Number.isFinite(price) || price <= 0) return 'No';
    if (!Number.isFinite(compareAt) || compareAt <= price) return 'No';
    return 'Yes';
  }

  /**
   * Check for unsafe characters in source values.
   *
   * @param {string} value
   * @returns {boolean}
   */
  function hasUnsafeCharacters(value) {
    if (typeof value !== 'string') return true;

    const codePoints = Array.from(value).map(char => char.codePointAt(0)).filter(cp => cp !== undefined);

    for (const cp of codePoints) {
      // C0 controls U+0000–U+001F
      if (cp >= 0x0000 && cp <= 0x001F) return true;
      // C1 controls U+007F–U+009F
      if (cp >= 0x007F && cp <= 0x009F) return true;
      // Bidi controls U+200E–U+200F, U+202A–U+202E, U+2066–U+2069
      if ((cp >= 0x200E && cp <= 0x200F) || (cp >= 0x202A && cp <= 0x202E) || (cp >= 0x2066 && cp <= 0x2069)) return true;
      // Zero-width U+200B–U+200D, U+2060
      if ((cp >= 0x200B && cp <= 0x200D) || cp === 0x2060) return true;
      // BOM U+FEFF
      if (cp === 0xFEFF) return true;
    }

    return false;
  }

  /**
   * Validate controlled source kind and value.
   * Rejects empty and whitespace-only values.
   * Enforces 1-200 Unicode code points and unsafe character controls.
   * Requires non-whitespace suffix for more_items, and the exact fixed label for
   * every other kind.
   *
   * @param {string} sourceKind
   * @param {string} sourceValue
   * @returns {boolean}
   */
  function validateControlledSource(sourceKind, sourceValue) {
    if (typeof sourceValue !== 'string') return false;

    // Reject whitespace-only values
    if (sourceValue.trim().length === 0) return false;

    const codePoints = Array.from(sourceValue);
    if (codePoints.length < 1 || codePoints.length > 200) return false;

    if (hasUnsafeCharacters(sourceValue)) return false;

    switch (sourceKind) {
      case 'collection':
        return sourceValue.trim().length > 0;
      case 'search':
        return sourceValue === 'Search';
      case 'cart':
        return sourceValue === 'Cart';
      case 'account':
        return sourceValue === 'My Account';
      case 'recommendation':
        return sourceValue === 'You may also like';
      case 'shop_all':
        return sourceValue === 'All';
      case 'recently_viewed':
        // A fixed label, not a prefix: the carousel has one heading and it is
        // the same on every PDP, so there is nothing to interpolate. The
        // French storefront still reports the English label, exactly as
        // "More Items to Consider - " does, because item_list_name is a GA4
        // dimension rather than customer-facing copy.
        return sourceValue === 'Recently viewed';
      case 'more_items':
        if (!sourceValue.startsWith('More Items to Consider - ')) return false;
        const suffix = sourceValue.slice('More Items to Consider - '.length);
        // Require non-whitespace suffix
        return suffix.trim().length > 0;
      default:
        return false;
    }
  }

  /**
   * Check if pathname is a localized product path.
   * Must be under typed window.Shopify.routes.root plus /products/ with nonempty suffix.
   * Handles root / and localized roots exactly.
   * Fails closed when route root is unavailable or malformed.
   *
   * @param {string} pathname
   * @returns {boolean}
   */
  function isProductPath(pathname) {
    if (typeof pathname !== 'string' || !pathname) return false;

    // Access typed window.Shopify.routes.root
    const shopifyRoutes = /** @type {any} */ (window).Shopify?.routes;
    if (!shopifyRoutes || typeof shopifyRoutes.root !== 'string') {
      // Fail closed when route root is unavailable
      return false;
    }

    const root = shopifyRoutes.root;

    // Reject malformed Shopify roots: require nonempty root starting and ending in '/'
    if (!root || !root.startsWith('/') || !root.endsWith('/')) return false;

    // Path must start with root
    if (!pathname.startsWith(root)) return false;

    // Extract path after root
    const pathAfterRoot = pathname.slice(root.length);

    // Match exactly 'products/<nonempty suffix>'
    if (!pathAfterRoot.startsWith('products/')) return false;

    // Must have nonempty product path suffix after 'products/'
    const afterProducts = pathAfterRoot.slice('products/'.length);
    return afterProducts.length > 0;
  }

  /**
   * Check if pathname is a localized account path: the account index itself, or
   * anything beneath it. Mirrors isProductPath, including failing closed when
   * the route root is unavailable or malformed.
   *
   * @param {string} pathname
   * @returns {boolean}
   */
  function isAccountPath(pathname) {
    if (typeof pathname !== 'string' || !pathname) return false;

    const shopifyRoutes = /** @type {any} */ (window).Shopify?.routes;
    if (!shopifyRoutes || typeof shopifyRoutes.root !== 'string') return false;

    const root = shopifyRoutes.root;
    if (!root || !root.startsWith('/') || !root.endsWith('/')) return false;
    if (!pathname.startsWith(root)) return false;

    const pathAfterRoot = pathname.slice(root.length);
    if (pathAfterRoot === 'account') return true;
    return pathAfterRoot.startsWith('account/');
  }

  /**
   * Return the canonical localized Cart pathname from Shopify's typed route root.
   *
   * @returns {string|null}
   */
  function getLocalizedCartPathname() {
    const shopifyRoutes = /** @type {any} */ (window).Shopify?.routes;
    if (!shopifyRoutes || typeof shopifyRoutes.root !== 'string') return null;

    const root = shopifyRoutes.root;
    if (!root || !root.startsWith('/') || !root.endsWith('/')) return null;

    return `${root}cart`;
  }

  /**
   * Validate complete token structure and return the validated source.
   *
   * The kind comes back alongside the value because the arrival page needs it to
   * report the surface the shopper clicked from. It was already validated by
   * validateControlledSource above; it was simply being discarded here.
   *
   * @param {any} token
   * @param {string} currentPathname
   * @param {number} now
   * @returns {{sourceKind: string, sourceValue: string, sourceIndex: number|null}|null}
   */
  function validateToken(token, currentPathname, now) {
    if (!token || typeof token !== 'object' || Array.isArray(token)) return null;

    // Exact eight-field schema
    const expectedKeys = ['committedAt', 'createdAt', 'sourceIndex', 'sourceKind', 'sourcePathname', 'sourceValue', 'targetPathname', 'v'];
    const actualKeys = Object.keys(token).sort();
    if (actualKeys.length !== expectedKeys.length) return null;
    for (let i = 0; i < expectedKeys.length; i++) {
      if (actualKeys[i] !== expectedKeys[i]) return null;
    }

    // Cast to typed token after schema validation
    const t = /** @type {OriginToken} */ (token);

    // Version must be 1
    if (t.v !== 1) return null;

    // Validate controlled source and optional one-based initiating-list index.
    if (!validateControlledSource(t.sourceKind, t.sourceValue)) return null;
    if (t.sourceIndex !== null && (!Number.isInteger(t.sourceIndex) || t.sourceIndex <= 0)) return null;

    // Source is a pathname captured by the controlled writer, never a URL,
    // query, fragment, or caller-constructed non-string value.
    if (
      typeof t.sourcePathname !== 'string' ||
      !t.sourcePathname.startsWith('/') ||
      t.sourcePathname.includes('?') ||
      t.sourcePathname.includes('#')
    ) return null;

    // Target must match current pathname
    if (t.targetPathname !== currentPathname) return null;

    // Source must differ from target
    if (t.sourcePathname === t.targetPathname) return null;

    // Target must be product path
    if (!isProductPath(t.targetPathname)) return null;

    // Validate timestamps
    if (!Number.isInteger(t.createdAt) || t.createdAt <= 0) return null;
    if (!Number.isInteger(t.committedAt) || t.committedAt <= 0) return null;
    if (t.createdAt > t.committedAt) return null;
    if (t.committedAt > now) return null;

    // Age must be <= 120000
    const age = now - t.createdAt;
    if (age > TOKEN_TTL_MS) return null;

    return { sourceKind: t.sourceKind, sourceValue: t.sourceValue, sourceIndex: t.sourceIndex };
  }

  /**
   * Clear pending owner and cancel timer.
   *
   * @param {PendingOwner} owner
   */
  function clearPendingOwner(owner) {
    if (owner.timerId !== null) {
      clearTimeout(owner.timerId);
      owner.timerId = null;
    }
    if (pendingOwner === owner) {
      pendingOwner = null;
    }
  }

  /**
   * Private closure builder for view items. Fail-closed: requires all four badges and availability.
   * itemId must reject whitespace-only while preserving valid bytes.
   *
   * @param {NormalizedViewSnapshot} snapshot
   * @returns {ViewItem|null}
   */
  function buildViewItemInternal(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return null;

    // Required base fields - reject whitespace-only
    if (typeof snapshot.itemId !== 'string' || snapshot.itemId.trim().length === 0) return null;
    if (typeof snapshot.itemName !== 'string' || snapshot.itemName.trim().length === 0) return null;
    if (typeof snapshot.itemVariant !== 'string' || snapshot.itemVariant.trim().length === 0) return null;
    if (typeof snapshot.brand !== 'string' || snapshot.brand.trim().length === 0) return null;
    if (typeof snapshot.category !== 'string' || snapshot.category.trim().length === 0) return null;
    if (typeof snapshot.category2 !== 'string' || snapshot.category2.trim().length === 0) return null;
    if (typeof snapshot.category3 !== 'string' || snapshot.category3.trim().length === 0) return null;

    // Price must be valid positive
    const price = Number(snapshot.priceCents);
    if (!Number.isFinite(price) || price <= 0) return null;
    const decimalPrice = price / 100;

    // Exact normalized badge validation: require exact Yes or No for all four badges
    // Ontario Grown: exact 'Yes' or 'No' (incomplete authority suppressed)
    if (snapshot.ontarioGrown !== 'Yes' && snapshot.ontarioGrown !== 'No') return null;

    // Craft: exact 'Yes' or 'No' (incomplete authority suppressed)
    if (snapshot.craft !== 'Yes' && snapshot.craft !== 'No') return null;

    // Price Drop: exact 'Yes' or 'No' (always present)
    if (snapshot.priceDrop !== 'Yes' && snapshot.priceDrop !== 'No') return null;

    // New: exact 'Yes' or 'No' (incomplete authority suppressed)
    if (snapshot.new !== 'Yes' && snapshot.new !== 'No') return null;

    // Availability: exact three states (always present)
    const validAvailability = ['In-Stock', 'In-Store Only', 'Out of Stock'];
    if (!validAvailability.includes(snapshot.itemAvailability)) return null;

    /** @type {ViewItem} */
    const item = {
      item_id: snapshot.itemId,
      item_name: snapshot.itemName,
      item_brand: snapshot.brand,
      item_category: snapshot.category,
      item_category2: snapshot.category2,
      item_category3: snapshot.category3,
      item_variant: snapshot.itemVariant,
      price: decimalPrice,
      currency: 'CAD',
      index: Number.isInteger(snapshot.index) && snapshot.index > 0 ? snapshot.index : 1,
      ontario_grown: snapshot.ontarioGrown,
      craft: snapshot.craft,
      price_drop: snapshot.priceDrop,
      new: snapshot.new,
      item_availability: snapshot.itemAvailability,
    };

    // Optional list name: absent allowed, present malformed suppresses entire item
    if (snapshot.itemListName !== undefined) {
      if (typeof snapshot.itemListName !== 'string' || snapshot.itemListName.trim().length === 0) return null;
      const listCodePoints = Array.from(snapshot.itemListName);
      if (listCodePoints.length < 1 || listCodePoints.length > 200 || hasUnsafeCharacters(snapshot.itemListName)) return null;
      item.item_list_name = snapshot.itemListName;
    }

    return item;
  }

  /**
   * Build a view without dropping the event when product or variant metadata
   * is incomplete. Every value that exists is preserved and unavailable values
   * remain explicit empty strings in the data layer.
   *
   * @param {NormalizedViewSnapshot} snapshot
   * @returns {ViewItem|null}
   */
  function buildIncompleteViewItemInternal(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return null;

    const text = function (value) {
      if (value === undefined || value === null) return '';
      return String(value);
    };
    const cents = Number(snapshot.priceCents);

    return {
      item_id: text(snapshot.itemId),
      item_name: text(snapshot.itemName),
      item_brand: text(snapshot.brand),
      item_category: text(snapshot.category),
      item_category2: text(snapshot.category2),
      item_category3: text(snapshot.category3),
      item_variant: text(snapshot.itemVariant),
      price: Number.isFinite(cents) && cents > 0 ? cents / 100 : 0,
      currency: 'CAD',
      index: Number.isInteger(snapshot.index) && snapshot.index > 0 ? snapshot.index : 1,
      ontario_grown: normalizeBooleanString(snapshot.ontarioGrown),
      craft: normalizeBooleanString(snapshot.craft),
      price_drop: normalizeBooleanString(snapshot.priceDrop),
      new: normalizeBooleanString(snapshot.new),
      item_availability: text(snapshot.itemAvailability),
      item_list_name: text(snapshot.itemListName || 'My Account'),
    };
  }

  function normalizeAddAvailability(raw) {
    const explicit = raw?.itemAvailability;
    if (['In-Stock', 'In-Store Only', 'Out of Stock'].includes(explicit)) return explicit;
    if (raw?.variantInStore === true || raw?.variantInStore === 'true') return 'In-Store Only';
    if (raw?.variantAvailable === false || raw?.variantAvailable === 'false') return 'Out of Stock';
    if (raw?.variantAvailable === true || raw?.variantAvailable === 'true') return 'In-Stock';
    return '';
  }

  /**
   * Private closure builder for add items. Fail-closed: requires all four badges, derives from raw.
   * itemId must reject whitespace-only while preserving valid bytes.
   *
   * @param {RawAddItem} raw
   * @returns {AddItem|null}
   */
  function buildAddItemInternal(raw) {
    if (!raw || typeof raw !== 'object') return null;

    // Required base fields - reject whitespace-only
    if (typeof raw.itemId !== 'string' || raw.itemId.trim().length === 0) return null;
    if (typeof raw.itemName !== 'string' || raw.itemName.trim().length === 0) return null;
    if (typeof raw.itemVariant !== 'string' || raw.itemVariant.trim().length === 0) return null;
    if (typeof raw.brand !== 'string' || raw.brand.trim().length === 0) return null;
    if (typeof raw.category !== 'string' || raw.category.trim().length === 0) return null;
    if (typeof raw.category2 !== 'string' || raw.category2.trim().length === 0) return null;
    if (typeof raw.category3 !== 'string' || raw.category3.trim().length === 0) return null;

    // Price must be valid positive
    const price = Number(raw.priceCents);
    if (!Number.isFinite(price) || price <= 0) return null;
    const decimalPrice = price / 100;

    // Quantity must be positive integer
    const quantity = Number(raw.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) return null;

    // All four badges required (derive from raw)
    const ontarioGrown = normalizeBooleanString(raw.ontarioGrown);
    if (ontarioGrown === undefined) return null;

    const craft = normalizeBooleanString(raw.craft);
    if (craft === undefined) return null;

    const compareAtAdd = raw.compareAtCents !== undefined ? raw.compareAtCents : raw.priceCents;
    const priceDrop = normalizePriceDrop(compareAtAdd, raw.priceCents);
    if (priceDrop === undefined) return null;

    const newBadge = normalizeNew(raw.variantStatus);
    if (newBadge === undefined) return null;

    /** @type {AddItem} */
    const item = {
      item_id: raw.itemId,
      item_name: raw.itemName,
      item_brand: raw.brand,
      item_variant: raw.itemVariant,
      item_category: raw.category,
      item_category2: raw.category2,
      item_category3: raw.category3,
      price: decimalPrice,
      ontario_grown: ontarioGrown,
      craft: craft,
      price_drop: priceDrop,
      new: newBadge,
      item_availability: normalizeAddAvailability(raw),
      quantity: quantity,
    };

    // Optional list name: absent allowed, present malformed suppresses entire item
    if (raw.itemListName !== undefined) {
      if (typeof raw.itemListName !== 'string' || raw.itemListName.trim().length === 0) return null;
      const listCodePoints = Array.from(raw.itemListName);
      if (listCodePoints.length < 1 || listCodePoints.length > 200 || hasUnsafeCharacters(raw.itemListName)) return null;
      item.item_list_name = raw.itemListName;
    }

    return item;
  }

  /**
   * Build an add item without suppressing the event when metadata is incomplete.
   * Available values are preserved and unavailable attributes remain explicit
   * empty strings in the data layer.
   *
   * @param {RawAddItem} raw
   * @returns {AddItem|null}
   */
  function buildIncompleteAddItemInternal(raw) {
    if (!raw || typeof raw !== 'object') return null;

    const text = function (value) {
      if (value === undefined || value === null) return '';
      return String(value);
    };
    const cents = Number(raw.priceCents);
    const quantity = Number(raw.quantity);

    const item = {
      item_id: text(raw.itemId),
      item_name: text(raw.itemName),
      item_brand: text(raw.brand),
      item_variant: text(raw.itemVariant),
      item_category: text(raw.category),
      item_category2: text(raw.category2),
      item_category3: text(raw.category3),
      price: Number.isFinite(cents) && cents > 0 ? cents / 100 : 0,
      ontario_grown: text(normalizeBooleanString(raw.ontarioGrown) ?? raw.ontarioGrown),
      craft: text(normalizeBooleanString(raw.craft) ?? raw.craft),
      price_drop: text(normalizePriceDrop(raw.compareAtCents, raw.priceCents)),
      new: text(normalizeNew(raw.variantStatus) ?? raw.variantStatus),
      item_availability: normalizeAddAvailability(raw),
      quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 0,
    };

    if (raw.itemListName !== undefined) item.item_list_name = text(raw.itemListName);
    return item;
  }

  /**
   * Private closure builder for remove items. Consumes normalized immutable snapshot.
   * Requires exact Yes/No badges, forces Cart context.
   * itemId must reject whitespace-only while preserving valid bytes.
   *
   * @param {RemoveSnapshot} snapshot
   * @returns {RemoveItem|null}
   */
  function buildRemoveItemInternal(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return null;

    // Required base fields - reject whitespace-only
    if (typeof snapshot.itemId !== 'string' || snapshot.itemId.trim().length === 0) return null;
    if (typeof snapshot.itemName !== 'string' || snapshot.itemName.trim().length === 0) return null;
    if (typeof snapshot.itemVariant !== 'string' || snapshot.itemVariant.trim().length === 0) return null;
    if (typeof snapshot.brand !== 'string' || snapshot.brand.trim().length === 0) return null;
    if (typeof snapshot.category !== 'string' || snapshot.category.trim().length === 0) return null;
    if (typeof snapshot.category2 !== 'string' || snapshot.category2.trim().length === 0) return null;
    if (typeof snapshot.category3 !== 'string' || snapshot.category3.trim().length === 0) return null;

    // Price must be valid positive decimal (already converted from cents)
    const price = Number(snapshot.priceCents);
    if (!Number.isFinite(price) || price <= 0) return null;
    const decimalPrice = price / 100;

    // Quantity must be positive integer
    const quantity = Number(snapshot.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) return null;

    // Exact Yes/No badges required (no derivation, consume normalized)
    if (snapshot.ontarioGrown !== 'Yes' && snapshot.ontarioGrown !== 'No') return null;
    if (snapshot.craft !== 'Yes' && snapshot.craft !== 'No') return null;
    if (snapshot.priceDrop !== 'Yes' && snapshot.priceDrop !== 'No') return null;
    if (snapshot.new !== 'Yes' && snapshot.new !== 'No') return null;

    // Force Cart context
    /** @type {RemoveItem} */
    const removeItem = {
      item_id: snapshot.itemId,
      item_name: snapshot.itemName,
      item_brand: snapshot.brand,
      item_variant: snapshot.itemVariant,
      item_category: snapshot.category,
      item_category2: snapshot.category2,
      item_category3: snapshot.category3,
      price: decimalPrice,
      ontario_grown: snapshot.ontarioGrown,
      craft: snapshot.craft,
      price_drop: snapshot.priceDrop,
      new: snapshot.new,
      quantity: quantity,
      item_list_name: 'Cart',
    };

    return removeItem;
  }

  /**
   * Build a cart-step item without suppressing quantity events when row metadata
   * is incomplete. Missing attributes remain explicit empty strings.
   *
   * @param {RemoveSnapshot} snapshot
   * @returns {RemoveItem|null}
   */
  function buildIncompleteCartStepItemInternal(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return null;

    const text = function (value) {
      if (value === undefined || value === null) return '';
      return String(value);
    };
    const cents = Number(snapshot.priceCents);
    const quantity = Number(snapshot.quantity);

    return {
      item_id: text(snapshot.itemId),
      item_name: text(snapshot.itemName),
      item_brand: text(snapshot.brand),
      item_variant: text(snapshot.itemVariant),
      item_category: text(snapshot.category),
      item_category2: text(snapshot.category2),
      item_category3: text(snapshot.category3),
      price: Number.isFinite(cents) && cents > 0 ? cents / 100 : 0,
      ontario_grown: normalizeBooleanString(snapshot.ontarioGrown),
      craft: normalizeBooleanString(snapshot.craft),
      price_drop: normalizeBooleanString(snapshot.priceDrop),
      new: normalizeBooleanString(snapshot.new),
      quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 0,
      item_list_name: 'Cart',
    };
  }

  /**
   * Build cart line properties from raw metadata. Returns a property map.
   * Derives each of the exact ten approved private properties independently.
   * Omits unavailable text fields while always writing the four boolean badge
   * properties as Yes or No. Missing or malformed badge metadata defaults to No.
   * Never writes empty values. Returns null only when raw metadata is unavailable.
   * Never includes name, variant, location, or availability.
   *
   * @param {RawCartMetadata} raw
   * @returns {Record<string, string>|null}
   */
  function buildCartLinePropertiesInternal(raw) {
    if (!raw || typeof raw !== 'object') return null;

    /** @type {Record<string, string>} */
    const properties = {};
    let hasAnyProperty = false;

    // _item_id (required, reject whitespace-only)
    if (typeof raw.itemId === 'string' && raw.itemId.trim().length > 0) {
      properties._item_id = raw.itemId;
      hasAnyProperty = true;
    }

    // _item_brand (required, reject whitespace-only)
    if (typeof raw.brand === 'string' && raw.brand.trim().length > 0) {
      properties._item_brand = raw.brand;
      hasAnyProperty = true;
    }

    // _item_category (required, reject whitespace-only)
    if (typeof raw.category === 'string' && raw.category.trim().length > 0) {
      properties._item_category = raw.category;
      hasAnyProperty = true;
    }

    // _item_category2 (required, reject whitespace-only)
    if (typeof raw.category2 === 'string' && raw.category2.trim().length > 0) {
      properties._item_category2 = raw.category2;
      hasAnyProperty = true;
    }

    // _item_category3 (required, reject whitespace-only)
    if (typeof raw.category3 === 'string' && raw.category3.trim().length > 0) {
      properties._item_category3 = raw.category3;
      hasAnyProperty = true;
    }

    // The four boolean badge properties are always persisted. Missing or
    // malformed source metadata defaults to No rather than removing the key.
    properties._ontario_grown = normalizeBooleanString(raw.ontarioGrown);
    properties._craft = normalizeBooleanString(raw.craft);
    properties._price_drop = normalizePriceDrop(raw.compareAtCents, raw.priceCents);
    properties._new = normalizeNew(raw.variantStatus);
    hasAnyProperty = true;

    // _item_list_name (optional, validate safe 1-200 nonblank characters)
    if (typeof raw.itemListName === 'string' && raw.itemListName.trim().length > 0) {
      const listCodePoints = Array.from(raw.itemListName);
      if (listCodePoints.length >= 1 && listCodePoints.length <= 200 && !hasUnsafeCharacters(raw.itemListName)) {
        properties._item_list_name = raw.itemListName;
        hasAnyProperty = true;
      }
    }

    // Return null only when no approved value could be set
    return hasAnyProperty ? properties : null;
  }

  /**
   * Install capture-phase navigation listener and pagehide commit handler.
   * Writes pending tokens, handles preventDefault cancellation, and commits on pagehide.
   */
  function installControlledOriginWriter() {
    /**
     * @param {MouseEvent} event
     */
    function handleClick(event) {
      // Only trusted primary unmodified clicks
      if (event.isTrusted !== true) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      // Already prevented
      if (event.defaultPrevented) return;

      // Find anchor
      if (!event.target) return;
      const targetElement = /** @type {Element} */ (event.target);
      if (typeof targetElement.closest !== 'function') return;
      const anchorElement = targetElement.closest('a[href]');
      if (!anchorElement) return;
      const anchor = /** @type {HTMLAnchorElement} */ (anchorElement);

      // No download links
      if (anchor.hasAttribute('download')) return;

      // Only absent, empty, or _self target
      const target = anchor.getAttribute('target');
      if (target && target !== '' && target !== '_self') return;

      // Get href and validate same-origin product path
      const href = anchor.href;
      let targetPathname;
      let targetSearch;
      let currentOrigin;
      try {
        currentOrigin = new URL(window.location.href).origin;
      } catch {
        return;
      }

      try {
        const targetUrl = new URL(href);
        if (targetUrl.origin !== currentOrigin) return;
        targetPathname = targetUrl.pathname;
        targetSearch = targetUrl.search;
      } catch {
        return;
      }

      /*
        Above the product-path check, and deliberately. The click-side publishers
        this serves are not boundary-scoped and they do not all navigate to
        products — the header account click goes to /account. Every same-origin
        link click is recorded here and each deferrer filters for its own
        destinations, which is what deferToNavigation's isAllowedTarget is for.
      */
      noteLinkClick(targetPathname, targetSearch, event);

      if (!isProductPath(targetPathname)) return;

      const documentPathname = window.location.pathname;

      // Find origin boundary with both attributes
      const boundaryElement = anchor.closest('[data-ocs-origin-kind][data-ocs-item-list-name]');
      if (!boundaryElement) return;
      const boundary = /** @type {HTMLElement} */ (boundaryElement);

      const sourceKind = boundary.dataset.ocsOriginKind;
      const sourceValue = boundary.dataset.ocsItemListName || '';
      const indexedElement = anchor.closest('[data-ocs-item-index]');
      const indexedBoundary = /** @type {HTMLElement|null} */ (indexedElement);
      const parsedSourceIndex = Number(
        indexedBoundary?.dataset.ocsItemIndex || boundary.dataset.ocsItemIndex,
      );
      const sourceIndex =
        Number.isInteger(parsedSourceIndex) && parsedSourceIndex > 0
          ? parsedSourceIndex
          : null;

      // Validate controlled source
      if (!sourceKind || !validateControlledSource(sourceKind, sourceValue)) return;

      // Cart drawer is a navigation surface independent of the underlying page.
      // Encode its canonical localized pathname while privately retaining the
      // actual document pathname for pagehide ownership verification.
      const isCartDrawer =
        sourceKind === 'cart' &&
        typeof boundary.closest === 'function' &&
        boundary.closest('ocs-cart-drawer-component') !== null;
      const sourcePathname = isCartDrawer ? getLocalizedCartPathname() : documentPathname;
      if (!sourcePathname) return;

      // Source must remain different from target. Canonical Cart preserves this
      // invariant when the drawer links back to the currently open PDP.
      if (sourcePathname === targetPathname) return;

      // Design mode: clear token, clear any existing pending owner/timer, and refuse
      const shopifyWrite = /** @type {any} */ (window).Shopify;
      if (shopifyWrite?.designMode) {
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {}
        // Clear any existing exact pending owner/timer before returning
        if (pendingOwner) {
          clearPendingOwner(pendingOwner);
        }
        return;
      }

      // Remove prior token before write
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {}

      // Clear prior owner
      if (pendingOwner) {
        clearPendingOwner(pendingOwner);
      }

      // Build pending token
      const now = Date.now();
      const token = {
        v: 1,
        sourceKind,
        sourceValue,
        sourceIndex,
        targetPathname,
        sourcePathname,
        createdAt: now,
        committedAt: null,
      };

      // Write synchronously
      let serialized;
      try {
        serialized = JSON.stringify(token);
        sessionStorage.setItem(STORAGE_KEY, serialized);
      } catch {
        // Storage failure permits navigation
        return;
      }

      // Bind owner object
      /** @type {PendingOwner} */
      const owner = {
        document: document,
        documentPathname,
        token,
        serialized,
        event,
        timerId: null,
      };
      pendingOwner = owner;

      // Schedule post-dispatch cancellation cleanup. A task, not a microtask:
      // during a real user click the browser runs a microtask checkpoint after
      // every listener, not at the end of the dispatch, so a microtask queued
      // from this capture-phase handler would read `defaultPrevented` before any
      // target- or bubble-phase listener had the chance to set it — and the
      // token of a canceled click would survive to mis-attribute the next PDP.
      setTimeout(function cleanupIfCanceled() {
        if (pendingOwner !== owner) return;

        if (event.defaultPrevented) {
          try {
            const current = sessionStorage.getItem(STORAGE_KEY);
            if (current === owner.serialized) {
              sessionStorage.removeItem(STORAGE_KEY);
              clearPendingOwner(owner);
            }
          } catch {
            // Storage access failure is a no-op
          }
        }
      }, 0);

      // Set 120001ms expiry timer
      owner.timerId = setTimeout(function expireToken() {
        if (pendingOwner !== owner) return;

        try {
          const current = sessionStorage.getItem(STORAGE_KEY);
          if (current === owner.serialized) {
            sessionStorage.removeItem(STORAGE_KEY);
            clearPendingOwner(owner);
          }
        } catch {
          // Storage access failure is a no-op
        }
      }, TOKEN_EXPIRY_MS);
    }

    // Pagehide commit handler
    function commitToken() {
      if (!pendingOwner) return;

      const owner = pendingOwner;

      // Validate owner document identity
      if (owner.document !== document) {
        clearPendingOwner(owner);
        return;
      }

      // Read current storage
      let current;
      try {
        current = sessionStorage.getItem(STORAGE_KEY);
      } catch {
        clearPendingOwner(owner);
        return;
      }

      // Verify exact token identity
      if (current !== owner.serialized) {
        clearPendingOwner(owner);
        return;
      }

      // Parse and validate the exact controlled-origin fields
      let token;
      try {
        token = JSON.parse(current);
      } catch {
        clearPendingOwner(owner);
        return;
      }

      if (!token || typeof token !== 'object' || Array.isArray(token)) {
        clearPendingOwner(owner);
        return;
      }

      // Exact eight-field schema with sorted keys
      const expectedKeys = ['committedAt', 'createdAt', 'sourceIndex', 'sourceKind', 'sourcePathname', 'sourceValue', 'targetPathname', 'v'];
      const actualKeys = Object.keys(token).sort();
      if (actualKeys.length !== expectedKeys.length) {
        clearPendingOwner(owner);
        return;
      }
      for (let i = 0; i < expectedKeys.length; i++) {
        if (actualKeys[i] !== expectedKeys[i]) {
          clearPendingOwner(owner);
          return;
        }
      }

      // Cast to typed token after schema validation
      const typedToken = /** @type {OriginToken} */ (token);

      // Fixed source/value and one-based index revalidation.
      if (!validateControlledSource(typedToken.sourceKind, typedToken.sourceValue)) {
        clearPendingOwner(owner);
        return;
      }
      if (
        typedToken.sourceIndex !== null &&
        (!Number.isInteger(typedToken.sourceIndex) || typedToken.sourceIndex <= 0)
      ) {
        clearPendingOwner(owner);
        return;
      }

      // The private owner must still belong to the active source document.
      // Drawer tokens use canonical Cart as their stored source pathname.
      if (owner.documentPathname !== window.location.pathname) {
        clearPendingOwner(owner);
        return;
      }

      if (typedToken.sourcePathname === typedToken.targetPathname) {
        clearPendingOwner(owner);
        return;
      }

      // Target must be a localized product path
      if (!isProductPath(typedToken.targetPathname)) {
        clearPendingOwner(owner);
        return;
      }

      // createdAt must be integer <= now
      const now = Date.now();
      if (!Number.isInteger(typedToken.createdAt) || typedToken.createdAt <= 0) {
        clearPendingOwner(owner);
        return;
      }
      if (typedToken.createdAt > now) {
        clearPendingOwner(owner);
        return;
      }

      // Inclusive age <= 120000
      const age = now - typedToken.createdAt;
      if (age > TOKEN_TTL_MS) {
        clearPendingOwner(owner);
        return;
      }

      // committedAt must be null before commit
      if (typedToken.committedAt !== null) {
        clearPendingOwner(owner);
        return;
      }

      // Commit: integer timestamp
      typedToken.committedAt = now;

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(typedToken));
      } catch {
        // Storage failure leaves no owner
      }

      clearPendingOwner(owner);
    }

    // Standard addEventListener for click and pagehide
    document.addEventListener('click', handleClick, true);
    window.addEventListener('pagehide', commitToken);
  }

  /**
   * Claim document initialization exactly once before any validation or publication.
   * Returns explicit claim result.
   *
   * @param {Document} doc
   * @returns {{claimed: boolean, duplicate: boolean, itemListName: string|undefined, originLocation: string|undefined, deferredView: object|undefined}}
   */
  function claimPdpInitialization(doc) {
    // Check duplicate first
    if (initializedDocuments.has(doc)) {
      return { claimed: true, duplicate: true, itemListName: undefined, originLocation: undefined, deferredView: undefined };
    }

    // Claim immediately before any other work
    initializedDocuments.add(doc);

    // Design mode clears token and returns no attribution
    const shopifyDesign = /** @type {any} */ (window).Shopify;
    if (shopifyDesign?.designMode) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(DEFERRED_VIEW_KEY);
      } catch {}
      return { claimed: true, duplicate: false, itemListName: undefined, originLocation: undefined, deferredView: undefined };
    }

    const currentPathname = window.location.pathname;
    const now = Date.now();

    // Consumed before the token is read, and on every path out of here, because
    // the two are independent: a deferred view with no token (order history, a
    // card on a surface that declares no boundary) must still be published, and
    // a token with no deferred view (a PLP hit, a search suggestion) must still
    // produce this page's own view.
    const deferredView = consumeDeferredViewItem(currentPathname, now);

    // Read and immediately remove before parse
    let stored;
    try {
      stored = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      return { claimed: true, duplicate: false, itemListName: undefined, originLocation: undefined, deferredView };
    }

    if (!stored) {
      return { claimed: true, duplicate: false, itemListName: undefined, originLocation: undefined, deferredView };
    }

    // Parse and validate
    let token;
    try {
      token = JSON.parse(stored);
    } catch {
      return { claimed: true, duplicate: false, itemListName: undefined, originLocation: undefined, deferredView };
    }

    const validated = validateToken(token, currentPathname, now);
    if (!validated) {
      return { claimed: true, duplicate: false, itemListName: undefined, originLocation: undefined, deferredView };
    }

    return {
      claimed: true,
      duplicate: false,
      itemListName: validated.sourceValue,
      itemIndex: validated.sourceIndex,
      originLocation: locationForOriginKind(validated.sourceKind),
      deferredView,
    };
  }

  /**
   * High-level PDP initialization: claim first, stop immediately on duplicate,
   * then validate, update state, and publish view exactly once.
   *
   * @param {Object} options
   * @param {HTMLElement} options.pdpRoot - PDP root element to update with item_list_name
   * @param {NormalizedViewSnapshot} options.normalizedSelectedItem - Normalized selected item data
   * @returns {boolean} - True if view was published, false otherwise
   */
  function initializePdpView(options) {
    // Claim the document FIRST before validating options or any metadata
    const claimResult = claimPdpInitialization(document);

    // Stop immediately on duplicate with zero reads and zero publication
    if (claimResult.duplicate) return false;

    // Now validate options (first invalid options/metadata attempt still claimed
    // and consumed once). A deferred view survives invalid options, because it
    // was built somewhere else out of somewhere else's metadata and needs
    // nothing from this page — losing the shopper's only view because this
    // page's own snapshot is broken would be the worse failure.
    const validOptions = !!options && typeof options === 'object';
    if (!validOptions && !claimResult.deferredView) return false;

    const { pdpRoot, normalizedSelectedItem } = validOptions ? options : {};

    // Update PDP root with valid consumed origin (data-item-list-name)
    if (pdpRoot && typeof pdpRoot === 'object' && pdpRoot.dataset && typeof pdpRoot.dataset === 'object' && claimResult.itemListName) {
      pdpRoot.dataset.itemListName = claimResult.itemListName;
    }

    // The click that brought the shopper here built this view already, at the
    // surface it happened on, and handed it over rather than publishing it into
    // an unloading document: a homepage carousel reports Homepage, order history
    // reports My Account, an Algolia PLP hit reports PLP. Published verbatim,
    // because the origin knew both the surface and the exact variant the shopper
    // had selected on the card, and this page knows neither.
    //
    // After the dataset write, not before. item_list_name attribution still has
    // to reach the page from the token, because the add_to_cart published from
    // here reads it, and that is true whichever view gets published.
    //
    // The location below is 'PDP' only when nothing upstream named a surface:
    // no token, or a kind with no mapping. A cart or PLP click has no product
    // card to publish its own view, so its token is the only thing that knows
    // where the shopper came from.
    const payload = claimResult.deferredView || createViewItemPayload(
      {
        ...normalizedSelectedItem,
        index: claimResult.itemIndex,
        itemListName: claimResult.itemListName,
      },
      claimResult.originLocation || 'PDP'
    );

    if (!payload) return false;

    // Publish view exactly once
    return publishOcsAnalyticsEvent('ocs:view_item', payload);
  }

  /**
   * Public atomic payload method: create the view-item payload for one product.
   *
   * The PDP is not the only surface that reports a product view — defect G15 is
   * a view from My Account, where the shopper clicks through to a product from
   * their order history. Production reported that too: its view_item was a click
   * on any `a[href*="/products/"]`, with `location` derived from whichever
   * container the link sat in.
   *
   * Same whitelist as the cart events: an unrecognised location returns null and
   * suppresses the event, so a typo is silence rather than a new GA4 dimension
   * value.
   *
   * @param {NormalizedViewSnapshot} snapshot
   * @param {string} location
   * @returns {object|null}
   */
  function createViewItemPayload(snapshot, location) {
    if (!VALID_LOCATIONS.includes(location)) return null;

    // Prefer the fully validated shape, but never suppress a product view solely
    // because one or more item attributes are unavailable.
    const viewItem =
      buildViewItemInternal(snapshot) || buildIncompleteViewItemInternal(snapshot);
    if (!viewItem) return null;

    return {
      location: location,
      items: [viewItem],
    };
  }

  /**
   * Every `location` GA4 accepts on a cart event.
   *
   * `Cart Flyout` is production's name for the drawer, taken from js_theme.js's
   * `data-id-prefix` mapping (`flyout--cart__` → `Cart Flyout`, `cart-page__` →
   * `Cart`). It matters because production's quantity steppers reported the two
   * surfaces separately and the reports distinguish them.
   *
   * Not a free-text field: an unrecognised value returns null and suppresses the
   * whole event, so a typo is silence rather than a new GA4 dimension value.
   */
  const VALID_LOCATIONS = ['PDP', 'PLP', 'Cart', 'Cart Flyout', 'My Account', 'Homepage'];

  /**
   * The `location` a view published on arrival should report, per origin kind.
   *
   * A card that publishes its own view on click carries `data-ocs-location` and
   * never reaches this. These are the surfaces with no card to speak for them:
   * the Algolia PLP renders its own markup, and cart lines are not product
   * cards, so both used to arrive reporting 'PDP' — as if the shopper had typed
   * the URL.
   *
   * Deliberately partial. `search` and `more_items` are absent because their
   * answers are not derivable from the kind alone: search has no entry in
   * VALID_LOCATIONS, and More Items to Consider appears on the homepage and the
   * PDP. `recommendation` is the controlled "You may also like" surface and is
   * intentionally the only view source mapped to Cart Flyout. `account` is absent because
   * the order row publishes its own view on click, with 'My Account'.
   * `recently_viewed` is absent because it is right: that carousel only renders
   * on a product template, so a click inside it did happen on a PDP.
   */
  const ORIGIN_KIND_LOCATIONS = {
    cart: 'Cart',
    collection: 'PLP',
    recommendation: 'Cart Flyout',
    shop_all: 'PLP',
  };

  /**
   * Resolve an origin kind to its arrival location, defaulting to 'PDP'.
   *
   * Fails closed the same way the whitelist does: an unmapped or malformed kind
   * reports a direct arrival rather than guessing a surface.
   *
   * @param {*} sourceKind
   * @returns {string}
   */
  function locationForOriginKind(sourceKind) {
    const mapped = Object.prototype.hasOwnProperty.call(ORIGIN_KIND_LOCATIONS, sourceKind)
      ? ORIGIN_KIND_LOCATIONS[sourceKind]
      : undefined;
    // The whitelist check is not redundant: a future map entry outside
    // VALID_LOCATIONS would suppress the whole event rather than mislabel it.
    return VALID_LOCATIONS.includes(mapped) ? mapped : 'PDP';
  }

  /**
   * Every `atc_type` GA4 tag 358 accepts.
   *
   * Production distinguished the two reorder paths: `.reorder-form--item` submit
   * sent `single`, `.reorder-form--order` submit sent `reorder` on each line.
   * Every other add path sent nothing, which is the empty string here. GA4's
   * `atc_type` parameter has been waiting on tag 358 the whole time — defect G17.
   *
   * Same fail-closed rule as the location whitelist.
   */
  const VALID_ATC_TYPES = ['', 'single', 'reorder'];

  /**
   * Public atomic payload method: create add-to-cart payload.
   * Validates nonempty array, exact location, and exact atc_type. Complete items
   * retain the normalized contract; incomplete item attributes pass through
   * without suppressing the event.
   *
   * @param {RawAddItem[]} rawItems
   * @param {string} location
   * @param {string} [atcType] defaults to ''; the My Account reorder paths pass
   *   'single' or 'reorder'.
   * @returns {object|null}
   */
  function createAddToCartPayload(rawItems, location, atcType) {
    if (!Array.isArray(rawItems) || rawItems.length === 0) return null;

    // Validate exact location
    if (!VALID_LOCATIONS.includes(location)) return null;

    // Validate exact atc_type. Defaulting keeps every caller that shipped before
    // this ticket on its original, empty-string behaviour.
    const resolvedAtcType = atcType === undefined ? '' : atcType;
    if (!VALID_ATC_TYPES.includes(resolvedAtcType)) return null;

    const items = [];
    let totalValue = 0;

    for (const raw of rawItems) {
      const item = buildAddItemInternal(raw) || buildIncompleteAddItemInternal(raw);
      if (!item) continue;

      items.push(item);
      totalValue += item.price * item.quantity;
    }
    if (items.length === 0) return null;

    return {
      currency: 'CAD',
      value: totalValue,
      location: location,
      atc_type: resolvedAtcType,
      items: items,
    };
  }

  /**
   * Public atomic payload method: create remove-from-cart payload.
   * Produces exact Section 3.3 contract with one complete item.
   *
   * `location` defaults to 'Cart' so every existing caller keeps its shipped
   * behaviour; the drawer's quantity stepper passes 'Cart Flyout'.
   *
   * @param {RemoveSnapshot} snapshot
   * @param {string} [location]
   * @returns {object|null}
   */
  function createRemoveFromCartPayload(snapshot) {
    return createCartStepPayload(snapshot, 'Cart');
  }

  /**
   * Public atomic payload method: the payload for one quantity step on a cart
   * line, in either direction.
   *
   * add_to_cart and remove_from_cart carry the identical five-key shape, so a
   * step up and a step down differ only in the event they are published under —
   * which is production's rule verbatim: `operation == -1 ? remove : add`. The
   * `quantity` on the snapshot is the *delta*, not the line's new total, again
   * as production did: its steppers always sent 1.
   *
   * Consumes the normalized cart-line snapshot (Yes/No badges), which is what
   * snippets/ocs-cart-item.liquid renders onto the row — not the raw
   * compare-at/variant-status shape createAddToCartPayload takes.
   *
   * @param {RemoveSnapshot} snapshot
   * @param {string} [location] defaults to 'Cart'; the drawer passes 'Cart Flyout'.
   * @returns {object|null}
   */
  function createCartStepPayload(snapshot, location) {
    const resolved = location === undefined ? 'Cart' : location;
    if (!VALID_LOCATIONS.includes(resolved)) return null;

    const item =
      buildRemoveItemInternal(snapshot) || buildIncompleteCartStepItemInternal(snapshot);
    if (!item) return null;

    const value = item.price * item.quantity;

    return {
      currency: 'CAD',
      value: value,
      location: resolved,
      atc_type: '',
      items: [item],
    };
  }

  /**
   * Public atomic payload method: create the view-cart payload.
   *
   * Production pushed this from js_theme.js on cart-flyout open, and GA4 tag 414
   * reads `items`, `value` and `dried_cannabis_equivalent` as **top-level**
   * dataLayer variables. The shape below is that push verbatim, including two
   * quirks that are contract rather than accident:
   *
   *   - an empty cart sends the string 'Not Available' where `items` would be,
   *     which is what `cartItems.length ? cartItems : "Not Available"` produced;
   *   - `index` is 0-based, from `index: cartItems.length` before the push.
   *
   * The item shape is deliberately the flat five-key one, not the enriched shape
   * add_to_cart and remove_from_cart use. Production sent five keys here and a
   * view_cart must not be suppressed because a line is missing its brand.
   *
   * @param {Array<{itemId?: unknown, itemName?: unknown, priceCents?: unknown, quantity?: unknown}>} rawItems
   * @param {{driedCannabisEquiv?: unknown, totalCents?: unknown}} [options]
   * @returns {object|null} null only when `rawItems` is not an array.
   */
  function createViewCartPayload(rawItems, options) {
    if (!Array.isArray(rawItems)) return null;

    const settings = options && typeof options === 'object' ? options : {};
    const items = [];

    for (const raw of rawItems) {
      if (!raw || typeof raw !== 'object') continue;

      const priceCents = Number(raw.priceCents);
      const quantity = Number(raw.quantity);
      const itemId = typeof raw.itemId === 'string' ? raw.itemId.trim() : '';
      const itemName = typeof raw.itemName === 'string' ? raw.itemName.trim() : '';

      if (itemId === '' || itemName === '') continue;
      if (!Number.isFinite(priceCents) || priceCents < 0) continue;
      if (!Number.isInteger(quantity) || quantity <= 0) continue;

      items.push({
        item_id: itemId,
        item_name: itemName,
        price: priceCents / 100,
        quantity: quantity,
        index: items.length,
      });
    }

    const totalCents = Number(settings.totalCents);
    const driedCannabisEquiv =
      typeof settings.driedCannabisEquiv === 'string' ? settings.driedCannabisEquiv.trim() : '';

    return {
      items: items.length ? items : 'Not Available',
      driedCannabisEquiv: driedCannabisEquiv,
      value: Number.isFinite(totalCents) && totalCents >= 0 ? totalCents / 100 : 0,
    };
  }

  /**
   * Public persistence method: derive and set exact approved cart line properties.
   * Whitelist keys in fixed order. Never accepts prebuilt map. Omits unavailable
   * properties independently. Never sets item name, variant, location, or availability.
   *
   * @param {FormData} formData
   * @param {RawCartMetadata} rawMetadata
   * @returns {boolean}
   */
  function setCartLineProperties(formData, rawMetadata) {
    if (!(formData instanceof FormData)) return false;

    const properties = buildCartLinePropertiesInternal(rawMetadata);
    if (!properties) return false;

    // Set exact approved keys in fixed order via formData.set only
    const orderedKeys = [
      '_item_id',
      '_item_category',
      '_item_category2',
      '_item_category3',
      '_item_brand',
      '_ontario_grown',
      '_craft',
      '_price_drop',
      '_new',
      '_item_list_name',
    ];

    for (const key of orderedKeys) {
      if (properties.hasOwnProperty(key)) {
        const value = /** @type {Record<string, string>} */ (properties)[key];
        if (typeof value === 'string') {
          formData.set(`properties[${key}]`, value);
        }
      }
    }

    return true;
  }

  /**
   * Publish a header account click deferred by the previous page.
   *
   * Swept on every load rather than from an account-page entry point. The module
   * that publishes this event lives in another file and can only reach this one
   * through the frozen public API, which is deliberately closed at eight
   * methods — so the destination side is handled here instead of exposing a
   * ninth. Off the account section the envelope is consumed and discarded, which
   * is also what stops it leaking into a later page.
   */
  function publishDeferredAccountLocation() {
    try {
      const shopify = /** @type {any} */ (window).Shopify;

      // The theme editor is not a shopper journey. Same as deferToNavigation:
      // leave nothing behind.
      if (shopify?.designMode) {
        try {
          sessionStorage.removeItem(DEFERRED_ACCOUNT_KEY);
        } catch {}
        return;
      }

      const payload = consumeDeferredAccountLocation(window.location.pathname, Date.now());
      if (!payload) return;

      // Back through the public entry point rather than Shopify.analytics
      // directly, so this publish is validated exactly like any other. There is
      // no click in flight at load, so the deferral above cannot re-catch it.
      publishOcsAnalyticsEvent(ACCOUNT_LOCATION_EVENT, payload);
    } catch {
      // A destination that cannot report is the pre-existing behaviour, not a
      // reason to break the rest of the module's initialization.
    }
  }

  /**
   * Publish one deferred navigation event after the destination initializes.
   * Shopify's replay-queue publisher normally exists before this module runs,
   * but retry briefly instead of destructively consuming the event if it does not.
   *
   * @param {string} storageKey
   * @param {string} eventName
   * @param {(pathname: string, now: number) => object|undefined} consume
   * @param {number} attempt
   */
  function publishDeferredNavigationEvent(storageKey, eventName, consume, attempt) {
    try {
      const shopify = /** @type {any} */ (window).Shopify;

      if (shopify?.designMode) {
        try {
          sessionStorage.removeItem(storageKey);
        } catch {}
        return;
      }

      if (typeof shopify?.analytics?.publish !== 'function') {
        if (attempt < 5 && typeof setTimeout === 'function') {
          setTimeout(
            () => publishDeferredNavigationEvent(storageKey, eventName, consume, attempt + 1),
            100
          );
        }
        return;
      }

      let stored;
      try {
        stored = sessionStorage.getItem(storageKey);
      } catch {
        return;
      }

      const payload = consume(window.location.pathname, Date.now());
      if (!payload) return;

      // No click is in flight on load, so this cannot be deferred a second time.
      if (publishOcsAnalyticsEvent(eventName, payload)) return;

      // A transient publish failure must not turn destructive read into data
      // loss. Restore only if no newer event has claimed the key, then retry.
      try {
        if (stored && sessionStorage.getItem(storageKey) === null) {
          sessionStorage.setItem(storageKey, stored);
        }
      } catch {
        return;
      }
      if (attempt < 5 && typeof setTimeout === 'function') {
        setTimeout(
          () => publishDeferredNavigationEvent(storageKey, eventName, consume, attempt + 1),
          100
        );
      }
    } catch {
      // Reporting must never break destination-page initialization.
    }
  }

  function publishDeferredCta() {
    publishDeferredNavigationEvent(DEFERRED_CTA_KEY, CTA_EVENT, consumeDeferredCta, 0);
  }

  function publishDeferredAccountCta() {
    publishDeferredNavigationEvent(
      DEFERRED_ACCOUNT_CTA_KEY,
      ACCOUNT_CTA_EVENT,
      consumeDeferredAccountCta,
      0
    );
  }

  // Auto-install writer on load if in browser environment
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        installControlledOriginWriter();
        publishDeferredAccountLocation();
        publishDeferredCta();
        publishDeferredAccountCta();
      });
    } else {
      installControlledOriginWriter();
      publishDeferredAccountLocation();
      publishDeferredCta();
      publishDeferredAccountCta();
    }
  }

  // Expose minimal frozen high-level API: exactly eight behavior-level methods
  if (typeof window !== 'undefined') {
    /** @type {any} */ (window).OCSAnalyticsEvents = Object.freeze({
      publishOcsAnalyticsEvent,
      initializePdpView,
      createViewItemPayload,
      createAddToCartPayload,
      createRemoveFromCartPayload,
      createCartStepPayload,
      createViewCartPayload,
      setCartLineProperties,
    });
  }
})();
